/**
 * Pure derivations behind the «Панель» tab (TASK-5080).
 *
 * Everything here is a function of data the API already returned — no clock
 * reads, no fetching, no React. That is deliberate: the band's "is this card
 * shown" rule and the strip's "null is not zero" rule are the two things most
 * likely to regress, and a pure module is the only way to pin them with tests
 * that cannot be satisfied by a mock.
 */

import type { Route } from "@/types/routes";
import type { LiveVehicle } from "@/types/tracking";

/** The four problem kinds the «ТРЕБУЕТ РЕШЕНИЯ» band can raise. */
export type ActionCardKind = "unassigned" | "late" | "offline" | "drafts";

/**
 * One measured problem.
 *
 * `count` is always a real measurement: a card is BUILT only when its source
 * answered. A failed or absent source yields no card at all rather than a card
 * reading zero — see {@link buildActionCards}.
 */
export interface ActionCardData {
  kind: ActionCardKind;
  count: number;
  /**
   * Pre-resolved detail line, or null when the source gave nothing to say.
   * Built by the component (it needs `t`), not here — this module only decides
   * WHETHER there is a card and what number it carries.
   */
  detail: string | null;
}

/**
 * The inputs {@link buildActionCards} measures. Every field is
 * `number | null | undefined`:
 *
 * - a **number** is a measurement (`0` included — a real, measured zero),
 * - `null` / `undefined` is NO MEASUREMENT (request failed, still in flight, or
 *   the field does not exist on this backend yet).
 *
 * The distinction is the whole contract: a measured zero hides the card
 * (nothing to fix), an absent measurement ALSO hides it (we do not know, and
 * inventing a zero would claim we do). They produce the same output here but
 * for opposite reasons, and the metric strip renders them differently («0» vs
 * «—»), so they must stay distinct all the way through.
 */
export interface ActionCardSources {
  /** Orders in no route at all — `GET /v1/orders/unassigned-count/`. */
  unassignedCount: number | null | undefined;
  /**
   * Routes running behind schedule RIGHT NOW.
   *
   * **No source exists yet.** The backend has no route-level deviation on the
   * routes list (verified: `RouteSerializer` carries none), so this is `null`
   * in production today and the «Опаздывают» card never renders. It is wired
   * through rather than omitted so that landing the backend field is a
   * one-line change at the call site, not a re-plumb.
   *
   * TODO(backend): `deviation_counts.late` on `GET /v1/routes/`, computed
   * before pagination from `public_api/services_eta.py`'s `deviation_sec()` +
   * `classify_schedule_status()` (ETA of the next unvisited stop vs its
   * `scheduled_arrival_at`, ±`ON_TIME_TOLERANCE_SEC`). NOT a seventh
   * `status_counts` key — those six are disjoint and sum to the total, while
   * `late` is orthogonal to all of them.
   */
  lateCount: number | null | undefined;
  /** Vehicles whose tracker is silent — `fleet-summary`.`offline`. */
  offlineCount: number | null | undefined;
  /** Built-but-unsent routes — `status_counts.draft`. */
  draftCount: number | null | undefined;
}

/**
 * Builds the band's cards in mockup order, dropping every zero and every
 * unmeasured source.
 *
 * The zero rule is a hard acceptance criterion, not styling: a card at 0 is NOT
 * rendered — not greyed, not empty. All four absent means the whole band,
 * heading and badge included, is gone. The badge counts CARDS SHOWN, which is
 * `result.length` — never the sum of the counts.
 */
export function buildActionCards(
  sources: ActionCardSources,
  details: Partial<Record<ActionCardKind, string | null>> = {},
): ActionCardData[] {
  const order: Array<[ActionCardKind, number | null | undefined]> = [
    ["unassigned", sources.unassignedCount],
    ["late", sources.lateCount],
    ["offline", sources.offlineCount],
    ["drafts", sources.draftCount],
  ];

  const cards: ActionCardData[] = [];
  for (const [kind, raw] of order) {
    // `== null` catches both null and undefined; a measured 0 falls through to
    // the `> 0` test below and is correctly dropped as "nothing to fix".
    if (raw == null) continue;
    if (!Number.isFinite(raw) || raw <= 0) continue;
    cards.push({ kind, count: raw, detail: details[kind] ?? null });
  }
  return cards;
}

/**
 * Picks the vehicles the «Без связи» card names in its detail line.
 *
 * Reuses the server's own staleness verdict (`state === "offline"`, Sprint 202)
 * rather than re-deriving one from `offline_for_sec` against a threshold this
 * module would have to invent. A second definition is how the card and the
 * tracking page start disagreeing about who is offline.
 *
 * Sorted by the STALEST first and capped, because the detail line has room for
 * one or two names and the longest-silent tracker is the one worth naming.
 * `offline_for_sec` null sorts last — no measured age cannot outrank a measured
 * one.
 */
export function pickOfflineVehicles(
  vehicles: readonly LiveVehicle[] | undefined,
  limit = 1,
): LiveVehicle[] {
  if (!vehicles) return [];
  return vehicles
    .filter((v) => (v.state ?? v.status) === "offline")
    .slice()
    .sort((a, b) => (b.offline_for_sec ?? -1) - (a.offline_for_sec ?? -1))
    .slice(0, Math.max(0, limit));
}

/**
 * Parses a DRF `DecimalField`, which arrives as a JSON **STRING** (`"412.70"`).
 *
 * Returns null for absent/unparsable input rather than 0 — «—» and «0 км» mean
 * different things and the strip renders them differently.
 */
export function parseDecimal(
  value: string | number | null | undefined,
): number | null {
  if (value == null) return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** One segment of the «ХОД СМЕНЫ» bar. */
export interface ShiftSegment {
  kind: "delivered" | "late" | "undelivered" | "remaining";
  count: number;
  /** Share of `total`, 0–100. */
  percent: number;
}

/**
 * Inputs for the shift timeline. Same null-is-not-zero contract as
 * {@link ActionCardSources}.
 */
export interface ShiftProgressSources {
  /** Stops planned across the whole day — `sums.stops`. */
  totalStops: number | null | undefined;
  /**
   * Stops delivered (`confirmed`).
   *
   * TODO(backend): `sums.delivered` on `GET /v1/routes/` —
   * `Count("stops", filter=Q(stops__status="confirmed"))`, alongside the
   * existing `sums.undelivered`.
   */
  delivered: number | null | undefined;
  /**
   * Of those delivered, how many arrived outside their window.
   *
   * TODO(backend): its own filtered count. Deliberately NOT derived from
   * `otd_percent` — that percentage runs over a DIFFERENT denominator (stops
   * with a window AND an arrival), so multiplying it back out invents a number.
   */
  deliveredLate: number | null | undefined;
  /** Stops closed without delivery — `sums.undelivered`. */
  undelivered: number | null | undefined;
}

/**
 * Builds the stacked bar, or returns null when it cannot be drawn honestly.
 *
 * Null (→ the section is not rendered) whenever `totalStops` or `delivered` is
 * unmeasured, or the day has no stops: a timeline missing its main segment is
 * a picture of a shift that is not happening.
 *
 * `deliveredLate` and `undelivered` degrade individually — an absent one
 * contributes no segment and is folded into the others, so the bar still sums
 * to 100 % and never shows a mystery gap. `deliveredLate` is a SUBSET of
 * `delivered` (a late delivery is still a delivery), so the on-time segment is
 * `delivered - deliveredLate`, clamped at zero.
 */
export function buildShiftSegments(
  sources: ShiftProgressSources,
): { total: number; segments: ShiftSegment[] } | null {
  const total = sources.totalStops;
  const delivered = sources.delivered;
  if (total == null || delivered == null) return null;
  if (!Number.isFinite(total) || total <= 0) return null;

  const late = Math.max(0, Math.min(sources.deliveredLate ?? 0, delivered));
  const onTime = Math.max(0, delivered - late);
  const undelivered = Math.max(0, sources.undelivered ?? 0);
  // Clamped so a backend disagreement (delivered + undelivered > total) cannot
  // produce a negative remainder and a bar wider than its track.
  const remaining = Math.max(0, total - delivered - undelivered);

  const raw: Array<[ShiftSegment["kind"], number]> = [
    ["delivered", onTime],
    ["late", late],
    ["undelivered", undelivered],
    ["remaining", remaining],
  ];

  return {
    total,
    segments: raw
      .filter(([, count]) => count > 0)
      .map(([kind, count]) => ({
        kind,
        count,
        percent: (count / total) * 100,
      })),
  };
}

/**
 * Sorts routes so the worst deviation comes first — what «Посмотреть» and the
 * «сначала с отклонениями» pill both select (`?sort=deviation`).
 *
 * Returns a NEW array; the caller's list is never mutated.
 *
 * Routes with no measured deviation sort to the BOTTOM in their original
 * relative order, never interleaved among the on-time ones as if they were
 * measured at zero. Today that is every route (the field does not exist yet),
 * so the sort is a stable no-op in production until the backend lands — which
 * is the correct degradation: the pill reorders nothing rather than claiming an
 * order it cannot justify.
 */
export function sortByDeviation(routes: readonly Route[]): Route[] {
  return routes
    .map((route, index) => ({ route, index }))
    .sort((a, b) => {
      const da = routeDeviationSec(a.route);
      const db = routeDeviationSec(b.route);
      if (da == null && db == null) return a.index - b.index;
      if (da == null) return 1;
      if (db == null) return -1;
      if (da === db) return a.index - b.index;
      return db - da;
    })
    .map((entry) => entry.route);
}

/**
 * Reads a route's deviation in seconds, or null when the backend did not
 * measure one.
 *
 * TODO(backend): `deviation_sec: number | null` (+ a `schedule_status` verdict)
 * on the routes list serializer. Until then this always returns null, by
 * design: null means "not measured" and renders «—», which is honest. It must
 * never fall back to 0 — that would paint every route «по плану» and the table
 * would assert punctuality nobody checked.
 */
export function routeDeviationSec(route: Route): number | null {
  const value = route.deviation_sec;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
